import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AddEditModal = ({ isOpen, onClose, activeTab, editData, onSave }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending' }
  ];

  const platformOptions = [
    { value: 'AJIO', label: 'AJIO' },
    { value: 'MYNTRA', label: 'MYNTRA' },
    { value: 'AMAZON', label: 'Amazon' },
    { value: 'FLIPKART', label: 'Flipkart' },
    { value: 'OFFLINE', label: 'Offline Store' }
  ];

  const specializationOptions = [
    { value: 'digital-printing', label: 'Digital Printing' },
    { value: 'screen-printing', label: 'Screen Printing' },
    { value: 'sublimation', label: 'Sublimation Printing' },
    { value: 'heat-transfer', label: 'Heat Transfer' }
  ];

  const expertiseOptions = [
    { value: 'fabric-cutting', label: 'Fabric Cutting' },
    { value: 'pattern-cutting', label: 'Pattern Cutting' },
    { value: 'bulk-cutting', label: 'Bulk Cutting' },
    { value: 'precision-cutting', label: 'Precision Cutting' }
  ];

  const materialOptions = [
    { value: 'cotton', label: 'Cotton' },
    { value: 'polyester', label: 'Polyester' },
    { value: 'silk', label: 'Silk' },
    { value: 'wool', label: 'Wool' },
    { value: 'linen', label: 'Linen' },
    { value: 'rayon', label: 'Rayon' }
  ];

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        setFormData(editData);
      } else {
        setFormData(getInitialFormData());
      }
      setErrors({});
    }
  }, [isOpen, editData, activeTab]);

  const getInitialFormData = () => {
    const baseData = {
      name: '',
      code: '',
      contact: {
        phone: '',
        email: '',
        address: ''
      },
      status: 'active'
    };

    switch (activeTab) {
      case 'digital-print-operators':
        return {
          ...baseData,
          specialization: '',
          rate: '',
          experience: ''
        };
      case 'cutting-specialists':
        return {
          ...baseData,
          expertise: '',
          rate: '',
          experience: ''
        };
      case 'raw-material-suppliers':
        return {
          ...baseData,
          materials: [],
          kgRate: '',
          meterRate: '',
          conversionRatio: 4.9,
          paymentTerms: ''
        };
      case 'clients':
        return {
          ...baseData,
          platforms: [],
          onlineRate: '',
          offlineRate: '',
          paymentTerms: '',
          creditLimit: ''
        };
      case 'purchase-invoices':
        return {
          invoiceNumber: '',
          supplier: null,
          invoiceDate: new Date().toISOString().split('T')[0],
          dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          items: [{ material: '', quantity: '', rate: '', amount: '' }],
          subtotal: 0,
          taxRate: 18,
          taxAmount: 0,
          totalAmount: 0,
          paymentTerms: '30 days',
          notes: '',
          status: 'pending'
        };
      default:
        return {
          ...baseData,
          type: '',
          description: ''
        };
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleArrayChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (activeTab === 'purchase-invoices') {
      if (!formData.invoiceNumber?.trim()) {
        newErrors.invoiceNumber = 'Invoice number is required';
      }
      if (!formData.supplier) {
        newErrors.supplier = 'Supplier selection is required';
      }
      if (!formData.invoiceDate) {
        newErrors.invoiceDate = 'Invoice date is required';
      }
      if (!formData.items || formData.items.length === 0) {
        newErrors.items = 'At least one item is required';
      } else {
        formData.items.forEach((item, index) => {
          if (!item.material?.trim()) {
            newErrors[`items.${index}.material`] = 'Material is required';
          }
          if (!item.quantity || parseFloat(item.quantity) <= 0) {
            newErrors[`items.${index}.quantity`] = 'Valid quantity is required';
          }
          if (!item.rate || parseFloat(item.rate) <= 0) {
            newErrors[`items.${index}.rate`] = 'Valid rate is required';
          }
        });
      }
      if (!formData.totalAmount || parseFloat(formData.totalAmount) <= 0) {
        newErrors.totalAmount = 'Valid total amount is required';
      }
    } else {
      if (!formData.name?.trim()) {
        newErrors.name = 'Name is required';
      }

      if (!formData.contact?.phone?.trim()) {
        newErrors['contact.phone'] = 'Phone number is required';
      }

      if (!formData.contact?.email?.trim()) {
        newErrors['contact.email'] = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.contact.email)) {
        newErrors['contact.email'] = 'Invalid email format';
      }

      // Tab-specific validations
      switch (activeTab) {
        case 'digital-print-operators':
          if (!formData.specialization) {
            newErrors.specialization = 'Specialization is required';
          }
          if (!formData.rate) {
            newErrors.rate = 'Rate is required';
          }
          break;
        case 'cutting-specialists':
          if (!formData.expertise) {
            newErrors.expertise = 'Expertise is required';
          }
          if (!formData.rate) {
            newErrors.rate = 'Rate is required';
          }
          break;
        case 'raw-material-suppliers':
          if (!formData.materials?.length) {
            newErrors.materials = 'At least one material must be selected';
          }
          if (!formData.kgRate) {
            newErrors.kgRate = 'KG rate is required';
          }
          if (!formData.meterRate) {
            newErrors.meterRate = 'Meter rate is required';
          }
          break;
        case 'clients':
          if (!formData.platforms?.length) {
            newErrors.platforms = 'At least one platform must be selected';
          }
          if (!formData.onlineRate) {
            newErrors.onlineRate = 'Online rate is required';
          }
          if (!formData.offlineRate) {
            newErrors.offlineRate = 'Offline rate is required';
          }
          break;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Generate code if not editing
      if (!editData) {
        const prefix = activeTab.split('-')[0].toUpperCase().substring(0, 3);
        const timestamp = Date.now().toString().slice(-4);
        formData.code = `${prefix}${timestamp}`;
      }

      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    // Calculate amount for this item
    if (field === 'quantity' || field === 'rate') {
      const quantity = parseFloat(updatedItems[index].quantity) || 0;
      const rate = parseFloat(updatedItems[index].rate) || 0;
      updatedItems[index].amount = quantity * rate;
    }
    
    setFormData(prev => ({ ...prev, items: updatedItems }));
    calculateTotals(updatedItems);
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { material: '', quantity: '', rate: '', amount: '' }]
    }));
  };

  const removeItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, items: updatedItems }));
    calculateTotals(updatedItems);
  };

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const taxRate = parseFloat(formData.taxRate) || 0;
    const taxAmount = (subtotal * taxRate) / 100;
    const totalAmount = subtotal + taxAmount;
    
    setFormData(prev => ({
      ...prev,
      subtotal,
      taxAmount,
      totalAmount
    }));
  };

  const renderFormFields = () => {
    switch (activeTab) {
      case 'digital-print-operators':
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Operator Name"
                type="text"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={errors.name}
                required
                placeholder="Enter operator name"
              />
              <Select
                label="Specialization"
                options={specializationOptions}
                value={formData.specialization || ''}
                onChange={(value) => handleInputChange('specialization', value)}
                error={errors.specialization}
                required
                placeholder="Select specialization"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Rate per Meter (₹)"
                type="number"
                value={formData.rate || ''}
                onChange={(e) => handleInputChange('rate', parseFloat(e.target.value) || '')}
                error={errors.rate}
                required
                placeholder="Enter rate per meter"
              />
              <Input
                label="Experience (Years)"
                type="number"
                value={formData.experience || ''}
                onChange={(e) => handleInputChange('experience', e.target.value)}
                placeholder="Enter years of experience"
              />
            </div>
          </>
        );

      case 'cutting-specialists':
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Specialist Name"
                type="text"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={errors.name}
                required
                placeholder="Enter specialist name"
              />
              <Select
                label="Expertise"
                options={expertiseOptions}
                value={formData.expertise || ''}
                onChange={(value) => handleInputChange('expertise', value)}
                error={errors.expertise}
                required
                placeholder="Select expertise"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Rate per Piece (₹)"
                type="number"
                value={formData.rate || ''}
                onChange={(e) => handleInputChange('rate', parseFloat(e.target.value) || '')}
                error={errors.rate}
                required
                placeholder="Enter rate per piece"
              />
              <Input
                label="Experience (Years)"
                type="number"
                value={formData.experience || ''}
                onChange={(e) => handleInputChange('experience', e.target.value)}
                placeholder="Enter years of experience"
              />
            </div>
          </>
        );

      case 'raw-material-suppliers':
        return (
          <>
            <Input
              label="Supplier Name"
              type="text"
              value={formData.name || ''}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={errors.name}
              required
              placeholder="Enter supplier name"
              className="mb-4"
            />
            <Select
              label="Materials Supplied"
              options={materialOptions}
              value={formData.materials || []}
              onChange={(value) => handleArrayChange('materials', value)}
              error={errors.materials}
              required
              multiple
              searchable
              placeholder="Select materials"
              className="mb-4"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="KG Rate (₹)"
                type="number"
                value={formData.kgRate || ''}
                onChange={(e) => handleInputChange('kgRate', parseFloat(e.target.value) || '')}
                error={errors.kgRate}
                required
                placeholder="Rate per KG"
              />
              <Input
                label="Meter Rate (₹)"
                type="number"
                value={formData.meterRate || ''}
                onChange={(e) => handleInputChange('meterRate', parseFloat(e.target.value) || '')}
                error={errors.meterRate}
                required
                placeholder="Rate per meter"
              />
              <Input
                label="Conversion Ratio"
                type="number"
                value={formData.conversionRatio || 4.9}
                onChange={(e) => handleInputChange('conversionRatio', parseFloat(e.target.value) || 4.9)}
                description="1 KG = X Meters"
                placeholder="4.9"
              />
            </div>
            <Input
              label="Payment Terms"
              type="text"
              value={formData.paymentTerms || ''}
              onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
              placeholder="e.g., 30 days credit"
            />
          </>
        );

      case 'clients':
        return (
          <>
            <Input
              label="Client Name"
              type="text"
              value={formData.name || ''}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={errors.name}
              required
              placeholder="Enter client name"
              className="mb-4"
            />
            <Select
              label="Platforms"
              options={platformOptions}
              value={formData.platforms || []}
              onChange={(value) => handleArrayChange('platforms', value)}
              error={errors.platforms}
              required
              multiple
              placeholder="Select platforms"
              className="mb-4"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Online Rate (₹)"
                type="number"
                value={formData.onlineRate || ''}
                onChange={(e) => handleInputChange('onlineRate', parseFloat(e.target.value) || '')}
                error={errors.onlineRate}
                required
                placeholder="Rate for online platforms"
              />
              <Input
                label="Offline Rate (₹)"
                type="number"
                value={formData.offlineRate || ''}
                onChange={(e) => handleInputChange('offlineRate', parseFloat(e.target.value) || '')}
                error={errors.offlineRate}
                required
                placeholder="Rate for offline sales"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Payment Terms"
                type="text"
                value={formData.paymentTerms || ''}
                onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                placeholder="e.g., 15 days credit"
              />
              <Input
                label="Credit Limit (₹)"
                type="number"
                value={formData.creditLimit || ''}
                onChange={(e) => handleInputChange('creditLimit', parseFloat(e.target.value) || '')}
                placeholder="Maximum credit limit"
              />
            </div>
          </>
        );

      case 'purchase-invoices':
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Invoice Number"
                type="text"
                value={formData.invoiceNumber || ''}
                onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
                error={errors.invoiceNumber}
                required
                placeholder="Enter invoice number"
              />
              <Select
                label="Supplier"
                options={[
                  { value: 'sup001', label: 'Gujarat Textiles Ltd' },
                  { value: 'sup002', label: 'Mumbai Fabric House' },
                  { value: 'sup003', label: 'Delhi Cotton Mills' }
                ]}
                value={formData.supplier || ''}
                onChange={(value) => handleInputChange('supplier', { id: value, name: value })}
                error={errors.supplier}
                required
                placeholder="Select supplier"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Invoice Date"
                type="date"
                value={formData.invoiceDate || ''}
                onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
                error={errors.invoiceDate}
                required
              />
              <Input
                label="Due Date"
                type="date"
                value={formData.dueDate || ''}
                onChange={(e) => handleInputChange('dueDate', e.target.value)}
              />
            </div>

            {/* Items Section */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-foreground">Invoice Items</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addItem}
                  iconName="Plus"
                  iconPosition="left"
                  iconSize={16}
                >
                  Add Item
                </Button>
              </div>
              
              {formData.items?.map((item, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3 pb-3 border-b border-border last:border-b-0">
                  <Input
                    label="Material"
                    type="text"
                    value={item.material || ''}
                    onChange={(e) => handleItemChange(index, 'material', e.target.value)}
                    error={errors[`items.${index}.material`]}
                    placeholder="Material name"
                  />
                  <Input
                    label="Quantity"
                    type="number"
                    value={item.quantity || ''}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    error={errors[`items.${index}.quantity`]}
                    placeholder="Qty"
                  />
                  <Input
                    label="Rate (₹)"
                    type="number"
                    value={item.rate || ''}
                    onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                    error={errors[`items.${index}.rate`]}
                    placeholder="Rate"
                  />
                  <Input
                    label="Amount (₹)"
                    type="number"
                    value={item.amount || ''}
                    readOnly
                    className="bg-muted/50"
                  />
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(index)}
                      iconName="Trash2"
                      iconSize={16}
                      className="text-error hover:text-error"
                      disabled={formData.items.length === 1}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Totals Section */}
            <div className="bg-muted/25 border border-border rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Subtotal (₹)
                  </label>
                  <p className="text-lg font-semibold text-foreground">
                    ₹{formData.subtotal?.toLocaleString('en-IN') || '0'}
                  </p>
                </div>
                <div>
                  <Input
                    label="Tax Rate (%)"
                    type="number"
                    value={formData.taxRate || 18}
                    onChange={(e) => {
                      const rate = parseFloat(e.target.value) || 0;
                      handleInputChange('taxRate', rate);
                      calculateTotals(formData.items);
                    }}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Tax: ₹{formData.taxAmount?.toLocaleString('en-IN') || '0'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Total Amount (₹)
                  </label>
                  <p className="text-xl font-bold text-primary">
                    ₹{formData.totalAmount?.toLocaleString('en-IN') || '0'}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Payment Terms"
                type="text"
                value={formData.paymentTerms || ''}
                onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                placeholder="e.g., 30 days"
              />
              <Select
                label="Status"
                options={[
                  { value: 'pending', label: 'Pending' },
                  { value: 'paid', label: 'Paid' },
                  { value: 'overdue', label: 'Overdue' },
                  { value: 'cancelled', label: 'Cancelled' }
                ]}
                value={formData.status || 'pending'}
                onChange={(value) => handleInputChange('status', value)}
              />
            </div>

            <Input
              label="Notes"
              type="textarea"
              value={formData.notes || ''}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Additional notes or comments"
              rows={3}
            />
          </>
        );

      default:
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Party Name"
                type="text"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={errors.name}
                required
                placeholder="Enter party name"
              />
              <Input
                label="Type"
                type="text"
                value={formData.type || ''}
                onChange={(e) => handleInputChange('type', e.target.value)}
                placeholder="e.g., Vendor, Customer"
              />
            </div>
            <Input
              label="Description"
              type="text"
              value={formData.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Brief description"
            />
          </>
        );
    }
  };

  if (!isOpen) return null;

  return (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1001] p-4">
    <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
      
      {/* Modal Header */}
      <div className="flex items-center justify-between p-6 border-b border-border shrink-0">
        <div className="flex items-center space-x-3">
          <Icon name="Plus" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-foreground">
            {editData ? 'Edit' : 'Add New'}{' '}
            {activeTab
              .split('-')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')}
          </h2>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          iconName="X"
          iconSize={20}
        />
      </div>

      {/* Modal Content */}
      <form
        onSubmit={handleSubmit}
        className="p-6 overflow-y-auto flex-1"
      >
        <div className="space-y-4">
          {renderFormFields()}

          {/* Contact Info */}
          {activeTab !== 'purchase-invoices' && (
            <div className="border-t border-border pt-4">
              <h3 className="text-md font-medium text-foreground mb-4">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData.contact?.phone || ''}
                  onChange={(e) => handleInputChange('contact.phone', e.target.value)}
                  error={errors['contact.phone']}
                  required
                  placeholder="Enter phone number"
                />
                <Input
                  label="Email Address"
                  type="email"
                  value={formData.contact?.email || ''}
                  onChange={(e) => handleInputChange('contact.email', e.target.value)}
                  error={errors['contact.email']}
                  required
                  placeholder="Enter email address"
                />
              </div>
              <Input
                label="Address"
                type="text"
                value={formData.contact?.address || ''}
                onChange={(e) => handleInputChange('contact.address', e.target.value)}
                placeholder="Enter complete address"
                className="mt-4"
              />
            </div>
          )}

          {/* Status */}
          {activeTab !== 'purchase-invoices' && (
            <Select
              label="Status"
              options={statusOptions}
              value={formData.status || 'active'}
              onChange={(value) => handleInputChange('status', value)}
              required
            />
          )}
        </div>
      </form>

      {/* Modal Footer */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-border shrink-0">
        <Button variant="outline" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="default"
          onClick={handleSubmit}
          loading={isLoading}
          iconName="Save"
          iconPosition="left"
          iconSize={16}
        >
          {editData ? 'Update' : 'Save'}
        </Button>
      </div>
    </div>
  </div>
);

};

export default AddEditModal;